import React, { useEffect, useState } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Input, { inputVariants } from "@react/shared/ui/Input"
import { t } from "../helpers/format"
import Section from "./Section"

const Row = ({ icon, label, hint, children }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0">
    <span className="inline-grid place-items-center w-[28px] h-[28px] shrink-0 rounded-lg bg-slate-100 text-slate-500 text-[12px]">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-medium text-slate-800">{label}</div>
      {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
)

const Segmented = ({ value, options, disabled, onChange }) => (
  <div className={clsx("inline-flex p-0.5 rounded-md bg-slate-100 border border-slate-200", disabled && "opacity-60")}>
    {options.map(option => (
      <button
        key       = {option.value}
        type      = "button"
        disabled  = {disabled}
        onClick   = {() => option.value !== value && onChange(option.value)}
        className = {clsx(
          "h-7 px-2.5 rounded-[5px] text-[12px] font-medium transition-colors whitespace-nowrap disabled:cursor-not-allowed",
          value === option.value ? "bg-white text-slate-900 ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
)

const Switch = ({ checked, disabled, onChange, label }) => (
  <button
    type         = "button"
    role         = "switch"
    aria-checked = {checked}
    aria-label   = {label}
    disabled     = {disabled}
    onClick      = {() => onChange(!checked)}
    className    = {clsx(
      "relative inline-flex w-[36px] h-[20px] rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
      checked ? "bg-brand-500" : "bg-slate-300"
    )}
  >
    <span className={clsx("absolute top-[2px] left-[2px] w-[16px] h-[16px] rounded-full bg-white transition-transform", checked && "translate-x-[16px]")} />
  </button>
)

const AllowancesCard = ({ day, zones, editable, busy, onUpdate }) => {
  const [minutes, setMinutes] = useState(day.travel_minutes ?? "")
  const [comment, setComment] = useState(day.comment || "")
  useEffect(() => setMinutes(day.travel_minutes ?? ""), [day.travel_minutes])
  useEffect(() => setComment(day.comment || ""), [day.comment])

  const disabled  = !editable || busy
  const workHours = (day.entries || []).filter(entry => entry.kind === "work").reduce((sum, entry) => sum + (entry.hours || 0), 0)
  const autoMeal  = workHours >= (day.meal?.threshold || 0) && workHours > 0
  const mealValue = day.meal?.overridden ? (day.meal.value ? "yes" : "no") : "auto"
  const yesNo     = value => (value ? t("allowances.yes_label") : t("allowances.no_label"))

  return (
    <Section
      title = {t("allowances.title")}
      aside = {busy && <FontAwesomeIcon icon="circle-notch" spin className="text-[11px] text-slate-400" />}
    >
      <div className="rounded-lg border border-slate-200 bg-white">
        <Row icon="utensils" label={t("allowances.meal")} hint={t("allowances.meal_hint", { hours: day.meal?.threshold })}>
          <Segmented
            value    = {mealValue}
            disabled = {disabled}
            onChange = {value => onUpdate({ meal_allowance: value === "auto" ? "auto" : value === "yes" })}
            options  = {[
              { value: "auto", label: t("allowances.auto_value", { value: yesNo(autoMeal) }) },
              { value: "yes",  label: t("allowances.yes_label") },
              { value: "no",   label: t("allowances.no_label") },
            ]}
          />
        </Row>

        {zones.length > 0 && (
          <Row icon="route" label={t("allowances.zone")} hint={t("allowances.zone_hint")}>
            <select
              value     = {day.zone?.overridden ? day.zone.value || "" : ""}
              disabled  = {disabled}
              onChange  = {e => onUpdate({ travel_zone: e.target.value })}
              className = {clsx(inputVariants({ size: "sm" }), "w-[150px]")}
            >
              <option value="">{t("allowances.auto_value", { value: day.zone?.computed || t("allowances.none") })}</option>
              {zones.map(code => <option key={code} value={code}>{t("allowances.zone_code", { code })}</option>)}
            </select>
          </Row>
        )}

        <Row icon="car" label={t("allowances.travel")} hint={t("allowances.travel_hint")}>
          <div className="flex items-center gap-1.5">
            <Input
              type     = "number"
              min      = "0"
              step     = "5"
              size     = "sm"
              align    = "right"
              value    = {minutes}
              disabled = {disabled}
              onChange = {e => setMinutes(e.target.value)}
              onBlur   = {() => String(minutes) !== String(day.travel_minutes ?? "") && onUpdate({ travel_minutes: minutes === "" ? 0 : Number(minutes) })}
              onKeyDown = {e => e.key === "Enter" && e.currentTarget.blur()}
              className = "w-[80px]"
            />
            <span className="text-[12px] text-slate-500">{t("allowances.minutes")}</span>
          </div>
        </Row>

        <Row icon="id-card" label={t("allowances.driver")} hint={t("allowances.driver_hint")}>
          <Switch checked={!!day.driver} disabled={disabled} onChange={value => onUpdate({ driver: value })} label={t("allowances.driver")} />
        </Row>

        <div className="px-4 py-3">
          <textarea
            rows        = {2}
            value       = {comment}
            disabled    = {disabled}
            placeholder = {t("allowances.comment")}
            onChange    = {e => setComment(e.target.value)}
            onBlur      = {() => comment !== (day.comment || "") && onUpdate({ comment })}
            className   = {clsx(inputVariants({ size: "sm" }), "!h-auto py-1.5 resize-none")}
          />
        </div>
      </div>
    </Section>
  )
}

export default AllowancesCard
